import { Fragment } from 'react';
import { Check, X } from 'lucide-react';
import { OK, BAD, ACCENT } from '../../lib/uiTokens';

/**
 * Osa stavu dokladu — kde v procesu leží a co ho čeká.
 *
 * Nálepka stavu („Ke schválení") říká, v jaké přihrádce doklad je. Neřekne,
 * co už proběhlo ani co přijde, a přesně to člověk u dokladu řeší: jestli
 * na něm ještě něco visí.
 *
 * **Na desktopu je to časová osa přes celou šířku záznamu**, ne řada pilulek:
 * uzlíky na spojnici, popisky pod nimi. Sloučený pás se šesti ohraničenými
 * segmenty vypadal jako přepínač — člověk na něj klíval, i když se stav takhle
 * měnit nedá. Osa se neklíká, a tak taky musí vypadat. Plnou barvou se
 * nevyplňuje nic — plné barevné bloky se v aplikaci nepoužívají (vzor: aktivní
 * zastávka v kokpitu rozvozu).
 *
 * `zamitnuty` cestu nedokončí — je to slepá odbočka za „Ke schválení", takže
 * se kreslí červeně na místě, kde doklad skončil, a zbytek osy se nekreslí
 * vůbec (ukazovat „Zaplacen" u zamítnutého dokladu je lež).
 */

/** Kroky osy. `stavy` = stavy dokladu, které tenhle krok pokrývá. */
const KROKY = [
    { id: 'nahran',       label: 'Nahrán',       stavy: ['nahrany', 'skenuje_se'] },
    { id: 'rozpoznan',    label: 'Rozpoznán',    stavy: ['rozpoznan', 'k_doplneni'] },
    { id: 'ke_schvaleni', label: 'Ke schválení', stavy: ['ke_schvaleni'] },
    { id: 'schvalen',     label: 'Schválen',     stavy: ['schvaleny', 'ceka_na_platbu'] },
    { id: 'zaplacen',     label: 'Zaplacen',     stavy: ['zaplaceny'] },
    { id: 'zauctovan',    label: 'Zaúčtován',    stavy: ['zauctovany'] },
];

/** Index kroku, na kterém doklad stojí (−1 = neznámý stav → osa se nekreslí). */
const indexKroku = (stav) => KROKY.findIndex(k => k.stavy.includes(stav));

/**
 * Jak se stav jmenuje a kde v cestě leží — pro hlavičky, které si osu kreslí
 * po svém (mobilní doklad má místo koleček 3px pruh a stav řekne slovem).
 *
 * @returns {{label: string, index: number, celkem: number, zamitnut: boolean}|null}
 */
export function stavKroku(stav) {
    const zamitnut = stav === 'zamitnuty';
    const index = zamitnut ? 2 : indexKroku(stav);
    if (index < 0) return null;
    return {
        label: zamitnut ? 'Zamítnut' : KROKY[index].label,
        index,
        celkem: KROKY.length,
        zamitnut,
    };
}

export default function DokladOsa({ stav, aiConfidence = null, kompaktni = false, pruh = false, maly = false, isDark = false }) {
    const zamitnut = stav === 'zamitnuty';
    // Zamítnutý doklad prošel schvalovací frontou — proto se první tři kroky
    // kreslí jako hotové a osa se na nich zlomí.
    const aktivni = zamitnut ? 2 : indexKroku(stav);
    if (aktivni < 0) return null;

    /**
     * Telefon: šest koleček v řadě je dekorace, ne informace — bez popisků
     * nikdo nepozná, co který krok znamená, a celý 56px řádek jen opakuje
     * to, co vedle nich stojí slovy. Pruh ze šesti dílků řekne „kolikátý
     * z kolika" za 3 px; jméno kroku si hlavička napíše do popisného řádku.
     */
    if (pruh) {
        return (
            <div className="flex" style={{ gap: 3 }} role="img"
                aria-label={`Krok ${aktivni + 1} z ${KROKY.length}: ${zamitnut ? 'Zamítnut' : KROKY[aktivni].label}`}>
                {KROKY.map((k, i) => (
                    <span key={k.id} style={{
                        flexGrow: 1, height: 3, borderRadius: 999,
                        background: i < aktivni ? OK
                            : i === aktivni ? (zamitnut ? BAD : ACCENT)
                            : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)'),
                    }} />
                ))}
            </div>
        );
    }

    const hranice = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const jistota = aiConfidence != null ? Math.round(Number(aiConfidence) * 100) : null;

    /**
     * Mobil: šest popisků se do 390 px nevejde a zkratky by nikomu nepomohly.
     * Zbyde tedy tečka za hotový krok, prázdný kroužek za budoucí a jméno
     * jen u toho, na kterém doklad stojí — informace „kolikátý z kolika"
     * zůstane, místo neubere.
     */
    if (kompaktni) {
        return (
            <div className="flex items-center" style={{ gap: 5, flexWrap: 'wrap' }}>
                {KROKY.map((k, i) => {
                    if (i === aktivni) {
                        return (
                            <span key={k.id} className="inline-flex items-center rounded-full font-bold"
                                style={{
                                    padding: '3px 9px', fontSize: 11, flexShrink: 0,
                                    background: `${zamitnut ? BAD : ACCENT}2e`,
                                    border: `1px solid ${zamitnut ? BAD : ACCENT}`,
                                    color: zamitnut ? BAD : ACCENT,
                                }}>
                                {zamitnut ? 'Zamítnut' : k.label}
                            </span>
                        );
                    }
                    // Za zlomem u zamítnutého dokladu už nic nebylo — kreslit
                    // „Zaplacen" jako budoucí krok by slibovalo něco, co nepřijde.
                    if (zamitnut && i > aktivni) return null;
                    const hotovy = i < aktivni;
                    return (
                        <span key={k.id} title={k.label} className="inline-flex items-center justify-center rounded-full"
                            style={{
                                width: 16, height: 16, flexShrink: 0,
                                background: hotovy ? `${OK}2e` : 'transparent',
                                border: hotovy ? 'none' : `1px solid ${hranice}`,
                                color: OK,
                            }}>
                            {hotovy && <Check size={10} strokeWidth={3} />}
                        </span>
                    );
                })}
            </div>
        );
    }

    // Desktop: osa ve vlastním pruhu. Roztažená přes celou šířku záznamu
    // působila, že tam nepatří — na širokém okně byly mezi šesti drobnými
    // popisky půlmetrové mezery a z indikátoru se stala dekorace. Pruh má
    // proto vlastní plochu (patří ke kartě, neplave v ní) a strop šířky,
    // takže kroky drží pohromadě a oko pozná, kde doklad stojí.
    const viditelne = zamitnut ? KROKY.slice(0, aktivni + 1) : KROKY;
    const barvaAktivniho = zamitnut ? BAD : ACCENT;
    const tlumena = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)';
    // `maly` = osa sedí v hlavičce pod tlačítky akcí, takže musí být úsporná,
    // aby se tam vešla vedle názvu dodavatele.
    const uzel = maly ? 16 : 22;
    const pismo = maly ? 10 : 11.5;
    // Vlastní sklo tu být nesmí — sloupec detailu už jedno má a sčítala by se.
    const plochaPruhu = isDark
        ? { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }
        : { background: 'var(--surface-elevated)', border: '1px solid var(--border-subtle)' };

    return (
        <div className="flex items-start" style={{
            ...plochaPruhu, borderRadius: maly ? 10 : 12,
            padding: maly ? '9px 13px 8px' : '13px 18px 11px',
            maxWidth: maly ? 470 : 820,
        }} role="img"
            aria-label={`Krok ${aktivni + 1} z ${KROKY.length}: ${zamitnut ? 'Zamítnut' : KROKY[aktivni].label}`}>
            {viditelne.map((k, i) => {
                const hotovy = i < aktivni;
                const jeAktivni = i === aktivni;
                const prvni = i === 0;
                const posledni = i === viditelne.length - 1;
                const barva = jeAktivni ? barvaAktivniho : (hotovy ? OK : null);

                return (
                    <Fragment key={k.id}>
                        {/* Spojnice před uzlíkem. Zelená jen tam, kudy doklad opravdu
                            prošel — úsek k aktuálnímu kroku včetně. */}
                        {!prvni && (
                            <span style={{
                                flex: '1 1 0', minWidth: maly ? 8 : 14, height: 2, marginTop: uzel / 2 - 1,
                                background: i <= aktivni ? OK : tlumena,
                            }} />
                        )}

                        <span className="flex flex-col" style={{
                            flexShrink: 0, gap: maly ? 4 : 6, minWidth: 0,
                            alignItems: prvni ? 'flex-start' : posledni ? 'flex-end' : 'center',
                        }}>
                            <span className="inline-flex items-center justify-center rounded-full" style={{
                                width: uzel, height: uzel, flexShrink: 0,
                                background: jeAktivni ? `${barva}2e` : hotovy ? `${OK}2e` : 'transparent',
                                border: `${jeAktivni ? 2 : 1.5}px solid ${jeAktivni ? barva : hotovy ? `${OK}66` : tlumena}`,
                                color: barva || 'transparent',
                            }}>
                                {hotovy && <Check size={maly ? 10 : 13} strokeWidth={3} />}
                                {jeAktivni && zamitnut && <X size={maly ? 10 : 13} strokeWidth={3} />}
                                {jeAktivni && !zamitnut && (
                                    <span style={{ width: maly ? 6 : 8, height: maly ? 6 : 8, borderRadius: 999, background: barva }} />
                                )}
                            </span>

                            {/* Aktuální krok je pilulka, ne další drobný popisek —
                                je to jediná věc, kterou z osy člověk opravdu čte. */}
                            {/* Jistota AI stojí na **témže řádku** jako popisek kroku,
                                který ji vyrobil — jako druhý řádek rozhazovala výšku
                                celé osy kvůli jedinému kroku. */}
                            {jeAktivni ? (
                                <span className="whitespace-nowrap rounded-full font-bold" style={{
                                    padding: maly ? '1px 8px' : '2px 10px', fontSize: maly ? 10.5 : 12,
                                    background: `${barva}24`, border: `1px solid ${barva}`, color: barva,
                                }}>
                                    {zamitnut ? 'Zamítnut' : k.label}
                                    {jistota != null && k.id === 'rozpoznan' && (
                                        <span style={{ fontWeight: 500, opacity: 0.8 }}> · AI {jistota} %</span>
                                    )}
                                </span>
                            ) : (
                                <span className="whitespace-nowrap" style={{
                                    fontSize: pismo, fontWeight: 500, padding: maly ? '1px 0' : '3px 0',
                                    color: hotovy ? 'var(--text-secondary)' : 'var(--text-tertiary)',
                                }}>
                                    {k.label}
                                    {jistota != null && k.id === 'rozpoznan' && (
                                        <span style={{ color: 'var(--text-tertiary)' }}> · AI {jistota} %</span>
                                    )}
                                </span>
                            )}
                        </span>

                        {/* Zamítnutým dokladem cesta končí. Zbytek osy se nekreslí —
                            ukazovat „Zaplacen" by sliboval krok, který nepřijde — ale
                            stopa po něm zůstává tečkovaná, ať pruh drží tvar. */}
                        {posledni && zamitnut && (
                            <span style={{
                                flex: '1 1 0', minWidth: maly ? 8 : 14, height: 0, marginTop: uzel / 2,
                                borderTop: `2px dotted ${tlumena}`,
                            }} />
                        )}
                    </Fragment>
                );
            })}
        </div>
    );
}
